import { useState, useEffect } from "react";
import { Navigate } from "react-router";
import { useAuth } from "../../hooks/useAuth";
import { createBusiness, updateBusiness } from "../../api/business.api";
import { apiClient } from "../../api/client";
import { CATEGORIES, CATEGORY_LABELS, type Category } from "@abejon/shared";
import type { BusinessProfile } from "@abejon/shared";
import { AddressAutocomplete, type SelectedAddress } from "../../components/ui/AddressAutocomplete";

export function SettingsPage() {
  const { user, isLoading, isAuthenticated } = useAuth();
  const [profile, setProfile] = useState<BusinessProfile | null>(null);
  const [loadingProfile, setLoadingProfile] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");

  // Form state
  const [displayName, setDisplayName] = useState("");
  const [category, setCategory] = useState<Category>("barber");
  const [bio, setBio] = useState("");
  const [phone, setPhone] = useState("");
  const [address, setAddress] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [zip, setZip] = useState("");
  const [latitude, setLatitude] = useState<number | undefined>();
  const [longitude, setLongitude] = useState<number | undefined>();
  const [addressDisplay, setAddressDisplay] = useState("");

  useEffect(() => {
    if (!user || user.accountType !== "business") return;
    let cancelled = false;

    async function loadProfile() {
      try {
        const data = await apiClient<{ businesses: BusinessProfile[] }>(
          "/businesses?mine=true"
        ).catch(() => null);

        if (!cancelled && data?.businesses?.[0]) {
          setProfile(data.businesses[0]);
        }
      } catch {
        // No existing profile — will show create form
      } finally {
        if (!cancelled) setLoadingProfile(false);
      }
    }
    loadProfile();
    return () => { cancelled = true; };
  }, [user]);

  if (isLoading) {
    return (
      <div className="flex justify-center py-16">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-gray-200 border-t-gray-900" />
      </div>
    );
  }

  if (!isAuthenticated || user?.accountType !== "business") {
    return <Navigate to="/" replace />;
  }

  function handleAddressSelect(selected: SelectedAddress) {
    setAddress(selected.address);
    setCity(selected.city);
    setState(selected.state);
    setZip(selected.zip);
    setLatitude(selected.latitude);
    setLongitude(selected.longitude);
    setAddressDisplay(selected.displayName);
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    setMessage("");

    try {
      const data = {
        displayName,
        category,
        bio: bio || undefined,
        phone: phone || undefined,
        address: address || undefined,
        city: city || undefined,
        state: state || undefined,
        zip: zip || undefined,
        latitude,
        longitude,
      };

      if (profile) {
        const { business } = await updateBusiness(profile.slug, data);
        setProfile(business);
        setMessage("Profile updated!");
      } else {
        const { business } = await createBusiness(data);
        setProfile(business);
        setMessage("Profile created!");
      }
    } catch {
      setMessage("Failed to save profile");
    } finally {
      setSaving(false);
    }
  }

  // Populate form when profile loads
  useEffect(() => {
    if (profile) {
      setDisplayName(profile.displayName);
      setCategory(profile.category);
      setBio(profile.bio ?? "");
      setPhone(profile.phone ?? "");
      setAddress(profile.address ?? "");
      setCity(profile.city ?? "");
      setState(profile.state ?? "");
      setZip(profile.zip ?? "");
      setLatitude(profile.latitude ?? undefined);
      setLongitude(profile.longitude ?? undefined);
      // Build a display string from existing address fields
      const parts = [profile.address, profile.city, profile.state, profile.zip].filter(Boolean);
      setAddressDisplay(parts.join(", "));
    }
  }, [profile]);

  return (
    <div className="mx-auto max-w-lg py-8">
      <h1 className="mb-6 text-2xl font-bold">
        {profile ? "Edit Business Profile" : "Create Business Profile"}
      </h1>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700">
            Business Name
          </label>
          <input
            type="text"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            required
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-gray-900 focus:outline-none focus:ring-1 focus:ring-gray-900"
          />
        </div>

        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700">
            Category
          </label>
          <select
            value={category}
            onChange={(e) => setCategory(e.target.value as Category)}
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-gray-900 focus:outline-none focus:ring-1 focus:ring-gray-900"
          >
            {CATEGORIES.map((cat) => (
              <option key={cat} value={cat}>
                {CATEGORY_LABELS[cat]}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700">
            Bio
          </label>
          <textarea
            value={bio}
            onChange={(e) => setBio(e.target.value)}
            rows={3}
            maxLength={500}
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-gray-900 focus:outline-none focus:ring-1 focus:ring-gray-900"
          />
        </div>

        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700">
            Phone
          </label>
          <input
            type="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-gray-900 focus:outline-none focus:ring-1 focus:ring-gray-900"
          />
        </div>

        {/* Address autocomplete */}
        <AddressAutocomplete
          initialValue={addressDisplay}
          onSelect={handleAddressSelect}
        />

        {/* Auto-filled address details (read-only) */}
        {city && (
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-500">City</label>
              <p className="rounded-lg bg-gray-50 px-3 py-2 text-sm text-gray-700">{city}</p>
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-500">State</label>
              <p className="rounded-lg bg-gray-50 px-3 py-2 text-sm text-gray-700">{state}</p>
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-500">ZIP</label>
              <p className="rounded-lg bg-gray-50 px-3 py-2 text-sm text-gray-700">{zip || "—"}</p>
            </div>
          </div>
        )}

        {message && (
          <p className={`text-sm ${message.includes("Failed") ? "text-red-600" : "text-green-600"}`}>
            {message}
          </p>
        )}

        <button
          type="submit"
          disabled={saving}
          className="w-full rounded-lg bg-gray-900 px-4 py-2.5 font-medium text-white hover:bg-gray-800 disabled:opacity-50"
        >
          {saving ? "Saving..." : profile ? "Update Profile" : "Create Profile"}
        </button>

        {profile && (
          <p className="text-center text-sm text-gray-500">
            Your profile: <a href={`/b/${profile.slug}`} className="underline">/b/{profile.slug}</a>
          </p>
        )}
      </form>
    </div>
  );
}
